/**
 * ClassReport — 班级报告渲染组件（R1，AIQ 深蓝金边风格移植）
 *
 * 6 sections（已显式删素质 + 删作答质量）：
 *   1. 封面       cover
 *   2. 全员综合画像 recommendation.class_headline_tag + class_summary + highlights/concerns
 *   3. 团队总览   dashboard: KPIs + 四阶分布 + positions + 5 力均分 + score dist（无素质）
 *   4. 维度强弱   heatmap: rows × ability columns + overflow summary row
 *   5. 风险预警   risks: summary cards + top dims + risk items with student names
 *   6. 提升方向   recommendation.recommendations + action roadmap
 *   7. 封底
 *
 * 防御式：全字段可选链 + 兜底，绝不抛。LLM 列表字段一律 asArray 归一（企业报告教训）。
 */
import React from 'react';
import './class-report.css';

// ── helpers ────────────────────────────────────────────────────────────────

const L_GRADE_LABEL = {
  L1: 'L1 知晓尝试级',
  L2: 'L2 基础应用级',
  L3: 'L3 协同优化级',
  L4: 'L4 创新引领级',
};

const PRIORITY_LABEL = { P0: '立即开展', P1: '本季跟进', P2: '后续观察' };

// Roadmap-position labels (sequential slot names, distinct from P0/P1/P2 priority labels above)
const ROADMAP_LABEL = ['立即开展', '紧随其后', '本季跟进'];

const STEP_STYLE = ['urgent', 'warning', 'normal'];

// LLM 返回的"列表"字段可能是数组、字符串、或 null —— 一律归一成数组，防 .map/.join 崩。
function asArray(v) {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  return [v];
}

// 报告生成时间统一展示为北京时间（后端 generated_at 为 UTC ISO 串）。
function formatBeijingTime(iso) {
  if (!iso) return '';
  const s = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 19).replace('T', ' ');
  const bj = new Date(d.getTime() + 8 * 60 * 60 * 1000);
  const p = (n) => String(n).padStart(2, '0');
  return (
    `${bj.getUTCFullYear()}-${p(bj.getUTCMonth() + 1)}-${p(bj.getUTCDate())} ` +
    `${p(bj.getUTCHours())}:${p(bj.getUTCMinutes())}:${p(bj.getUTCSeconds())}`
  );
}

function heatCssClass(score) {
  if (score == null) return 'cr-hot-none';
  if (score >= 85) return 'cr-hot-1';
  if (score >= 70) return 'cr-hot-2';
  if (score >= 60) return 'cr-hot-3';
  if (score >= 50) return 'cr-hot-4';
  return 'cr-hot-5';
}

function dimBarClass(score) {
  if (score == null) return '';
  if (score < 50) return 'weak';
  if (score < 65) return 'mid';
  return 'strong';
}

function lGradePillClass(grade) {
  return `cr-lp cr-lp-${grade || 'L1'}`;
}

function scoreBucketColor(label = '') {
  const start = Number(String(label).split('-')[0]);
  if (Number.isNaN(start)) return '#1a3a6b';
  if (start >= 90) return '#15803d';
  if (start >= 70) return '#1d4ed8';
  if (start >= 60) return '#0f766e';
  if (start >= 40) return '#d97706';
  return '#dc2626';
}

// Derive L grade from score
function scoreToLGrade(score) {
  if (score == null) return null;
  if (score >= 85) return 'L4';
  if (score >= 70) return 'L3';
  if (score >= 55) return 'L2';
  return 'L1';
}

// ── Cover 四阶 helpers ─────────────────────────────────────────────────────
//
// cover.layer_distribution 为数组 [{ l_grade, count, pct }, ...]（始终 4 项 L1-L4）。
// 旧 / 测试 payload 可能传字符串或空 —— 下列 helper 归一所有情形，绝不抛。

/** 返回主导（最高 count）等级 key，无则 null。 */
function dominantLGrade(layerField) {
  if (!layerField) return null;
  if (typeof layerField === 'string') {
    return L_GRADE_LABEL[layerField] ? layerField : null;
  }
  if (!Array.isArray(layerField) || layerField.length === 0) return null;
  const best = [...layerField].sort((a, b) => (b.count || 0) - (a.count || 0))[0];
  return (best && best.count > 0) ? best.l_grade : null;
}

/** 紧凑分布串："L1×2 · L2×9 · L3×11 · L4×2"，空 / 字符串时 null。 */
function coverLGradeSummary(layerField) {
  if (!Array.isArray(layerField) || layerField.length === 0) return null;
  const parts = layerField
    .filter((item) => item && item.count > 0)
    .map((item) => `${item.l_grade}×${item.count}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ── Section 1: Cover ───────────────────────────────────────────────────────

function CoverSection({ cover }) {
  const {
    enterprise_name = '',
    class_name = '',
    team_avg_score,
    layer_distribution,   // 数组 [{l_grade,count,pct}] 或 legacy 字符串 / null
  } = cover || {};

  const scoreInt = team_avg_score != null ? Math.floor(team_avg_score) : '—';
  const scoreDec = team_avg_score != null
    ? ('.' + String(team_avg_score.toFixed(1)).split('.')[1])
    : '';

  const dominant = dominantLGrade(layer_distribution);
  const lLabel = dominant ? (L_GRADE_LABEL[dominant] || dominant) : null;
  const lSummary = coverLGradeSummary(layer_distribution);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="cr-page cr-cover">
      <div className="cr-brand-row">
        <div className="cr-brand">前哨 · AI 领导力测评</div>
        <div className="cr-brand">CLASS REPORT · {today.slice(0, 7)}</div>
      </div>
      <h1>
        前哨 AI 领导力测评报告
        <br />
        <span style={{ opacity: 0.6 }}>·班级版</span>
      </h1>
      <div className="cr-subtitle">{class_name}</div>

      <div className="cr-hero-score">
        <div className="cr-hero-score-label">团队均分 / 整体能力档位</div>
        <div className="cr-hero-score-row">
          <div className="cr-hero-score-num">
            {scoreInt}
            {scoreDec && <span style={{ fontSize: 48 }}>{scoreDec}</span>}
          </div>
          {lLabel && <div className="cr-hero-l-grade">{lLabel}</div>}
        </div>
        {lSummary && (
          <div className="cr-muted" style={{ marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
            等级分布：{lSummary}
          </div>
        )}
      </div>

      <div className="cr-meta-grid">
        <div className="cr-meta-cell">
          <div className="cr-meta-label">企业</div>
          <div className="cr-meta-value">{enterprise_name || '—'}</div>
        </div>
        <div className="cr-meta-cell">
          <div className="cr-meta-label">班级</div>
          <div className="cr-meta-value">{class_name || '—'}</div>
        </div>
        <div className="cr-meta-cell">
          <div className="cr-meta-label">报告生成</div>
          <div className="cr-meta-value">{cover?.report_date || today}</div>
        </div>
      </div>
    </div>
  );
}

// ── Section 2: 全员综合画像 ────────────────────────────────────────────────

function PortraitSection({ recommendation, risks, dashboard }) {
  const {
    class_headline_tag = '',
    class_summary = '',
  } = recommendation || {};

  const items = risks?.items || [];
  const extremeRisk = items.filter((x) => x.level === '极高');

  const summary = risks?.summary || {};
  const topRiskDims = summary.top_risk_dims || [];

  const completion = dashboard?.completion || {};
  const dimAvgs = dashboard?.dimension_avgs || [];

  const total = completion.total || 0;
  const completed = completion.completed || 0;
  const completionRate = total > 0 ? (completed / total) * 100 : null;

  const scoredDims = dimAvgs.filter((d) => d.avg_score != null);
  const strongestDim = scoredDims.length > 0
    ? scoredDims.reduce((best, d) => (d.avg_score > best.avg_score ? d : best))
    : null;

  const highlights = [];
  if (completionRate !== null && completionRate >= 70) {
    highlights.push(`完成率 ${completionRate.toFixed(0)}%，参与度健康`);
  }
  if (strongestDim && strongestDim.avg_score >= 70) {
    highlights.push(`${strongestDim.name} 表现最强（均分 ${strongestDim.avg_score}）`);
  }
  if (topRiskDims.length === 0 && dimAvgs.length > 0) {
    highlights.push('各维度整体表现稳定');
  }

  return (
    <div className="cr-page">
      <div className="cr-page-mark">OVERVIEW · 综述</div>
      <h2 className="cr-h2">全员综合画像</h2>

      {(class_headline_tag || class_summary) && (
        <div className="cr-headline-card">
          {class_headline_tag && (
            <div className="cr-headline-tag">{class_headline_tag}</div>
          )}
          {class_summary && (
            <div className="cr-headline-summary">{class_summary}</div>
          )}
        </div>
      )}

      <div className="cr-row">
        <div className="cr-col cr-card">
          <h3 className="cr-h3">本班亮点</h3>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 2, color: '#1e293b' }}>
            {highlights.length > 0
              ? highlights.map((h) => <li key={h}>{h}</li>)
              : <li className="cr-muted">暂无突出亮点</li>}
          </ul>
        </div>
        <div className="cr-col cr-card">
          <h3 className="cr-h3">本班关注</h3>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 2, color: '#1e293b' }}>
            {completionRate !== null && completionRate < 50 && (
              <li><b style={{ color: '#991b1b' }}>完成率偏低（{completionRate.toFixed(0)}%）</b>，建议督促未完成学员</li>
            )}
            {topRiskDims.slice(0, 3).map((d) => (
              <li key={d.dim_code}>
                <b style={{ color: '#991b1b' }}>{d.dim_name}</b>
                {' '}· {d.affected}/{d.total} 人偏低
              </li>
            ))}
            {extremeRisk.length > 0 && (
              <li>
                <b style={{ color: '#9a3412' }}>极高风险 {extremeRisk.length} 人</b>
                {' '}· 建议1v1关注
              </li>
            )}
            {completionRate !== null && completionRate >= 50 && topRiskDims.length === 0 && extremeRisk.length === 0 && (
              <li className="cr-muted">暂无突出关注点</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ── Section 3: 团队总览（无素质段）──────────────────────────────────────────

function TeamOverviewSection({ dashboard }) {
  const {
    completion = {},
    dimension_avgs = [],
    score_distribution = [],
    layer_distribution = [],
    position_top5 = [],
  } = dashboard || {};

  const { total = 0, completed = 0, in_progress = 0, not_started = 0 } = completion;
  const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : '0.0';

  const buckets = score_distribution;
  const maxBucketCount = Math.max(...buckets.map((x) => x.count || 0), 1);
  const totalLayered = layer_distribution.reduce((s, x) => s + (x.count || 0), 0);

  return (
    <div className="cr-page">
      <div className="cr-page-mark">PART 01</div>
      <h2 className="cr-h2">团队总览</h2>

      {/* KPI */}
      <div className="cr-kpi-grid">
        {[
          ['总人数', total, '#0f766e'],
          ['已完成', completed, '#15803d'],
          ['进行中', in_progress, '#b45309'],
          ['未开始', not_started, '#94a3b8'],
          ['完成率', completionRate, '#1a3a6b'],
        ].map(([label, value, color]) => (
          <div key={label} className="cr-kpi">
            <div className="cr-kpi-label">{label}</div>
            <div className="cr-kpi-value" style={{ color }}>
              {value}
              {label === '完成率' && <span className="cr-kpi-unit">%</span>}
              {(label === '总人数' || label === '已完成' || label === '进行中' || label === '未开始')
                && <span className="cr-kpi-unit">人</span>}
            </div>
          </div>
        ))}
      </div>

      {/* 四阶成熟度分布 */}
      {layer_distribution.length > 0 && (
        <div className="cr-card" style={{ marginBottom: 20 }}>
          <h3 className="cr-h3">团队画像 · 四阶成熟度分布</h3>
          <div className="cr-muted" style={{ marginBottom: 14, fontSize: 12 }}>
            基于 {totalLayered} 名已完成学员的个人总等级归档
          </div>
          <div className="cr-l-stack-row">
            <div className="cr-l-stack">
              {layer_distribution.map((l) => {
                const pct = totalLayered > 0 ? (l.count / totalLayered) * 100 : 0;
                const key = l.l_grade;
                const cls = `cr-l${key?.toLowerCase?.()?.replace('l', '') || '1'}`;
                return (
                  <div
                    key={key}
                    className={cls}
                    style={{ width: `${pct}%`, minWidth: l.count > 0 ? 40 : 0 }}
                  >
                    {l.count > 0 && `${key} · ${l.count}`}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="cr-l-legend">
            {layer_distribution.map((l) => {
              const cls = `cr-l${l.l_grade?.toLowerCase?.()?.replace('l', '') || '1'}`;
              return (
                <div key={l.l_grade} className="cr-l-legend-item">
                  <div className={`cr-l-dot ${cls}`} />
                  {L_GRADE_LABEL[l.l_grade] || l.l_grade} · {l.count} 人 · {l.pct}%
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 职位分布（素质段已删） */}
      {position_top5.length > 0 && (
        <div className="cr-card" style={{ marginBottom: 28 }}>
          <h3 className="cr-h3">学员职位分布 · Top 5</h3>
          <div className="cr-position-list">
            {position_top5.map((p) => (
              <div key={p.position} className="cr-position-row">
                <span>{p.position}</span>
                <b>{p.count} 人</b>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5 力均分 + L 等级 */}
      {dimension_avgs.length > 0 && (
        <div className="cr-card" style={{ marginBottom: 28 }}>
          <h3 className="cr-h3">五力均分明细</h3>
          <div style={{ marginTop: 12 }}>
            {dimension_avgs.map((d) => {
              const lGrade = scoreToLGrade(d.avg_score);
              const isWeak = d.avg_score != null && d.avg_score < 65;
              return (
                <div
                  key={d.code}
                  className={`cr-radar-score-row${isWeak ? ' weak' : ''}`}
                >
                  <span className={isWeak ? 'cr-dim-name' : ''} style={{ fontSize: 14 }}>
                    {d.name}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <b className={isWeak ? 'cr-dim-val' : ''} style={{ marginRight: 8 }}>
                      {d.avg_score ?? '—'}
                    </b>
                    {lGrade && (
                      <span className={lGradePillClass(lGrade)}>{lGrade}</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 20 }}>
            <h3 className="cr-h3" style={{ marginBottom: 10 }}>五力均分条形图</h3>
            {dimension_avgs.map((d) => (
              <div key={d.code} className="cr-dim-bar-row">
                <div style={{ fontSize: 13 }}>{d.name}</div>
                <div className={`cr-dim-bar ${dimBarClass(d.avg_score)}`}>
                  {d.avg_score != null && (
                    <div style={{ width: `${d.avg_score}%` }} />
                  )}
                </div>
                <div className="cr-dim-score">{d.avg_score ?? '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 总分分布 */}
      {buckets.length > 0 && (
        <div className="cr-card">
          <h3 className="cr-h3">总分分布</h3>
          <div className="cr-muted" style={{ marginBottom: 8, fontSize: 12 }}>
            已完成学员，按 10 分档划分
          </div>
          <div className="cr-score-dist">
            {buckets.map((b) => {
              const label = b.range || b.bucket;
              const count = b.count || 0;
              const barHeight = count
                ? Math.max(20, Math.round((count / maxBucketCount) * 140))
                : 3;
              const color = count ? scoreBucketColor(label) : '#cbd5e1';
              return (
                <div key={label} className="cr-bar">
                  <div className={`cr-bar-num${count === 0 ? ' zero' : ''}`}>{count}</div>
                  <div
                    className="cr-bar-fill"
                    style={{ height: barHeight, background: color }}
                  />
                  <div className="cr-bar-label">{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section 4: 维度强弱 / 热力图 ──────────────────────────────────────────

function HeatmapSection({ heatmap }) {
  const { dimensions = [], rows = [], overflow } = heatmap || {};

  return (
    <div className="cr-page">
      <div className="cr-page-mark">PART 02</div>
      <h2 className="cr-h2">维度强弱 · 学员 × 能力矩阵</h2>

      <div className="cr-card">
        <div className="cr-muted" style={{ marginBottom: 14 }}>
          已完成学员的能力得分热力图。颜色越绿分越高，越红分越低。
        </div>

        {rows.length === 0 && overflow == null ? (
          <div className="cr-muted">暂无已完成测评的学员</div>
        ) : (
          <div className="cr-heatmap-wrap">
            <table className="cr-heatmap">
              <thead>
                <tr>
                  <th className="cr-th-first">学员</th>
                  {dimensions.map((d) => (
                    <th key={d.code} title={d.name}>{d.name}</th>
                  ))}
                  <th className="cr-th-first" style={{ width: 60 }}>总分</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.student?.id ?? idx}>
                    <td className="cr-td-label">
                      {row.student?.display_name || '—'}
                      {row.student?.position ? `（${row.student.position}）` : ''}
                    </td>
                    {(row.scores || []).map((s, i) => (
                      <td key={i} title={s != null ? String(s) : '—'}>
                        <span className={`cr-h-cell ${heatCssClass(s)}`}>
                          {s != null ? s : '—'}
                        </span>
                      </td>
                    ))}
                    <td style={{ textAlign: 'center', fontWeight: 700, fontSize: 13 }}>
                      {row.total_score ?? '—'}
                    </td>
                  </tr>
                ))}

                {overflow && (
                  <tr className="cr-heatmap-overflow">
                    <td
                      colSpan={dimensions.length + 2}
                      style={{ padding: '12px 0', textAlign: 'center', color: '#64748b', fontStyle: 'italic', fontSize: 12 }}
                    >
                      其余 {overflow.count} 人 · 团队均分 {overflow.team_avg}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div className="cr-muted" style={{ marginTop: 14, fontSize: 11, display: 'flex', gap: 14, alignItems: 'center' }}>
          <span>得分图例：</span>
          {[
            ['cr-hot-5', '<50'],
            ['cr-hot-4', '50-59'],
            ['cr-hot-3', '60-69'],
            ['cr-hot-2', '70-84'],
            ['cr-hot-1', '≥85'],
          ].map(([cls, label]) => (
            <span key={label}>
              <span
                className={`cr-h-cell ${cls}`}
                style={{ display: 'inline-block', width: 14, height: 14, padding: 0, verticalAlign: 'middle' }}
              />
              {' '}{label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Section 5: 风险预警 ────────────────────────────────────────────────────

const RISK_LEVELS = ['极高', '高', '中', '低'];

function RisksSection({ risks }) {
  const { summary = {}, items = [] } = risks || {};
  const countsByLevel = summary.counts_by_level || {};
  const topDims = summary.top_risk_dims || [];

  return (
    <div className="cr-page">
      <div className="cr-page-mark">PART 03</div>
      <h2 className="cr-h2">风险预警 · 团队级摘要</h2>

      {/* Risk level summary cards */}
      <div className="cr-card" style={{ marginBottom: 20 }}>
        <div className="cr-muted" style={{ marginBottom: 14 }}>
          基于 RiskTemplate 规则引擎扫描已完成报告
        </div>
        <div className="cr-risk-cards">
          {RISK_LEVELS.map((lv) => (
            <div key={lv} className={`cr-risk-card cr-risk-${lv}`}>
              <div className="cr-risk-level">{lv}风险</div>
              <div>
                <span className="cr-risk-num">{countsByLevel[lv] ?? 0}</span>
                <span className="cr-risk-unit">人次</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="cr-row">
        {topDims.length > 0 && (
          <div className="cr-col cr-card">
            <h3 className="cr-h3">触达人数最多的维度</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topDims.slice(0, 5).map((d) => (
                <div key={d.dim_code} style={{
                  padding: '10px 14px',
                  background: '#fef2f2',
                  borderLeft: '3px solid #dc2626',
                  borderRadius: 4,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>{d.dim_name}</div>
                  <div style={{ fontSize: 13, color: '#7f1d1d', marginTop: 2 }}>
                    {d.affected} 人偏低 / {d.total} 人
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="cr-col cr-card">
          <h3 className="cr-h3">重点关注名单</h3>
          {items.length === 0 ? (
            <div className="cr-muted">暂无风险条目</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {items.map((it, idx) => (
                <div key={idx} style={{
                  borderLeft: `4px solid ${it.level === '极高' ? '#dc2626' : it.level === '高' ? '#f97316' : '#f59e0b'}`,
                  paddingLeft: 12,
                }}>
                  <div style={{ fontWeight: 700, color: '#1a3a6b', fontSize: 13 }}>
                    {it.student?.display_name || '—'}
                    {it.student?.position ? `（${it.student.position}）` : ''}
                    {' · '}{it.dim_name} {it.score} / 阈值 {it.threshold}
                    {' '}
                    <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 999, background: '#fee2e2', color: '#991b1b' }}>
                      {it.level}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', marginTop: 4, lineHeight: 1.6 }}>
                    {it.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Section 6: 提升方向 ────────────────────────────────────────────────────

function RecommendationSection({ recommendation }) {
  const {
    recommendations = [],
    team_weakness = [],
  } = recommendation || {};

  // Build dim code → name map
  const codeToName = {};
  team_weakness.forEach((w) => { codeToName[w.dim_code] = w.dim_name; });
  recommendations.forEach((r) => {
    asArray(r.target_dims).forEach((c) => { if (!codeToName[c]) codeToName[c] = c; });
    asArray(r.cross_dim_links).forEach((c) => { if (!codeToName[c]) codeToName[c] = c; });
  });

  // Action roadmap: top 3 from team_weakness sorted by affected_count desc
  const roadmap = [...team_weakness]
    .sort((a, b) => (b.affected_count || 0) - (a.affected_count || 0))
    .slice(0, 3);

  if (recommendations.length === 0 && roadmap.length === 0) return null;

  return (
    <div className="cr-page">
      <div className="cr-page-mark">PART 04 · 重点章节</div>
      <h2 className="cr-h2">提升方向建议（基于团队短板的诊断 + 方向）</h2>

      {/* Team weakness table */}
      {team_weakness.length > 0 && (
        <div className="cr-card" style={{ marginBottom: 20 }}>
          <h3 className="cr-h3">团队短板维度</h3>
          <div className="cr-muted" style={{ marginBottom: 14, fontSize: 12 }}>
            维度均分 &lt; 65 分，按短板严重程度排序
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                {['维度', '团队均分', '影响人数', '距阈值差'].map((h, i) => (
                  <th key={h} style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    padding: '8px 12px',
                    fontSize: 12,
                    color: '#64748b',
                    fontWeight: 500,
                    letterSpacing: 1,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {team_weakness.map((w) => (
                <tr key={w.dim_code} style={{ borderBottom: '1px dotted #e2e8f0' }}>
                  <td style={{ padding: 12 }}>{w.dim_name}</td>
                  <td style={{ textAlign: 'right', padding: 12, fontWeight: 700, color: '#991b1b' }}>
                    {w.avg_score}
                  </td>
                  <td style={{ textAlign: 'right', padding: 12, fontWeight: 600 }}>
                    {w.affected_count != null ? `${w.affected_count} 人` : '—'}
                  </td>
                  <td style={{ textAlign: 'right', padding: 12, color: '#991b1b' }}>
                    {w.avg_score != null ? (w.avg_score - 65).toFixed(1) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Improvement cards */}
      {recommendations.map((r, idx) => (
        <ImprovementCard key={idx} rec={r} codeToName={codeToName} />
      ))}

      {/* Action roadmap */}
      {roadmap.length > 0 && (
        <div className="cr-card" style={{ marginTop: 28 }}>
          <h3 className="cr-h3">行动路线图</h3>
          <div className="cr-muted" style={{ marginBottom: 18, fontSize: 12.5 }}>
            按紧急度排序，建议按此顺序推进。
          </div>
          <div className="cr-action-roadmap">
            {roadmap.map((w, idx) => (
              <React.Fragment key={w.dim_code}>
                {idx > 0 && <div className="cr-action-arrow">→</div>}
                <div className={`cr-action-step ${STEP_STYLE[idx] || 'normal'}`}>
                  <div className="cr-step-num">{idx + 1}</div>
                  <div className="cr-step-label">
                    步骤 {idx + 1} · {ROADMAP_LABEL[idx] || '跟进'}
                  </div>
                  <div className="cr-step-title">{w.dim_name}</div>
                  <div className="cr-step-meta">
                    影响 {w.affected_count} 人 · 团队均分 {w.avg_score}
                    {w.avg_score != null && ` · 距阈值 ${(w.avg_score - 65).toFixed(1)}`}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ImprovementCard({ rec, codeToName }) {
  const dimNames = asArray(rec.target_dims).map((c) => codeToName[c] || c);
  const linkNames = asArray(rec.cross_dim_links);
  const directions = asArray(rec.improvement_directions);
  const observable = asArray(rec.observable_behaviors).join('；');
  const ifSkipped = asArray(rec.if_skipped_risks).join('；');
  const priorityLabel = PRIORITY_LABEL[rec.priority] || rec.priority || '';

  return (
    <div className={`cr-imp-card ${rec.priority || 'P2'}`}>
      <div className="cr-imp-header">
        <span className={`cr-priority-pill ${rec.priority || 'P2'}`}>{priorityLabel}</span>
        <h3 className="cr-imp-title">{rec.title}</h3>
      </div>
      <div className="cr-imp-meta">
        影响人数 <b>{rec.affected_count}</b>
        {dimNames.length > 0 && (
          <span> · 覆盖维度：<b>{dimNames.join('、')}</b></span>
        )}
      </div>

      {rec.why_priority && (
        <div className="cr-imp-section">
          <div className="cr-imp-section-label">为什么是这个优先级</div>
          <div className="cr-imp-section-body">{rec.why_priority}</div>
        </div>
      )}
      {directions.length > 0 && (
        <div className="cr-imp-section">
          <div className="cr-imp-section-label">具体应提升的能力子项</div>
          <ul className="cr-imp-directions">
            {directions.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
      )}
      {observable && (
        <div className="cr-imp-section">
          <div className="cr-imp-section-label">提升后可观察的行为</div>
          <div className="cr-imp-section-body">{observable}</div>
        </div>
      )}
      {ifSkipped && (
        <div className="cr-imp-section">
          <div className="cr-imp-section-label">不做的业务风险</div>
          <div className="cr-imp-section-body">{ifSkipped}</div>
        </div>
      )}
      {linkNames.length > 0 && (
        <div className="cr-imp-section">
          <div className="cr-imp-section-label">联动维度</div>
          <div className="cr-imp-links">
            {linkNames.map((c) => (
              <span key={c} className="cr-imp-link-chip">↗ {codeToName[c] || c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section 7: 封底 ────────────────────────────────────────────────────────

function FooterSection({ recommendation, cover }) {
  const aiMeta = recommendation?.ai_meta || {};
  const generatedAt = recommendation?.generated_at || new Date().toISOString();
  const enterpriseName = cover?.enterprise_name || '';
  const className = cover?.class_name || '';
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="cr-page cr-footer-page">
      <div className="cr-seal">END OF REPORT</div>
      <div className="cr-footer-divider" />
      <p style={{ margin: '0 0 8px' }}>
        <b style={{ color: '#0f172a' }}>关于本报告</b>
      </p>
      <p style={{ margin: '0 0 8px' }}>
        本报告由前哨 AI 领导力测评系统基于已完成测评学员的数据自动生成；维度均分 / 分布 / 风险条目为系统聚合，提升方向部分由 AI 基于团队短板生成。
      </p>
      <p style={{ margin: '0 0 8px' }}>
        报告版本：<b>班级版</b>
        {aiMeta.prompt_version != null && ` · prompt v${aiMeta.prompt_version}`}
        {' · '}报告生成时间：<b>{formatBeijingTime(generatedAt)}</b>
      </p>
      {(enterpriseName || className) && (
        <p style={{ margin: '0 0 8px' }}>
          企业：<b>{enterpriseName}</b>
          {className && <span> · 班级：<b>{className}</b></span>}
        </p>
      )}
      <div className="cr-footer-divider" />
      <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>
        © {today.slice(0, 4)} 前哨科技有限公司 · 本报告含团队聚合数据，请按企业合规策略保管
      </p>
    </div>
  );
}

// ── Root export ────────────────────────────────────────────────────────────

export default function ClassReport({ payload, printMode }) {
  if (!payload) return null;

  const { cover, dashboard, heatmap, risks, recommendation } = payload;

  return (
    <div className={`cr-root${printMode ? ' print-mode' : ''}`}>
      <CoverSection cover={cover} />
      <PortraitSection recommendation={recommendation} risks={risks} dashboard={dashboard} />
      <TeamOverviewSection dashboard={dashboard} />
      <HeatmapSection heatmap={heatmap} />
      <RisksSection risks={risks} />
      <RecommendationSection recommendation={recommendation} />
      <FooterSection recommendation={recommendation} cover={cover} />
    </div>
  );
}
